import { ArrowLeftOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate, useParams,
} from 'react-router-dom';

import {
    usersMutations, usersQueries,
} from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, UserForm, UserFormRef,
} from '@/components';
import { AppRoute } from '@/constants';
import { useUserStore } from '@/stores';
import { canEditUser } from '@/utils';

export const UserEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const { user: currentUser } = useUserStore();
    const formRef = useRef<UserFormRef>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        nickname: string;
        name?: string | null;
        email?: string | null;
        is_active?: boolean;
        comment?: string | null;
        role: string;
        password?: string | null;
        password_confirmation?: string | null;
        new_password?: boolean | null;
    } | null>(null);

    const { data: user, isLoading } = useQuery({
        ...usersQueries.getOneUser(id || ''),
        refetchOnWindowFocus: false,
    });
    const updateMutationOptions = usersMutations.update(id || '');
    const { mutate: updateUser, isPending } = useMutation(updateMutationOptions);

    useEffect(() => {
        if (user && !canEditUser(currentUser, user)) {
            navigate(`${AppRoute.Users}/${id}`);
        }
    }, [user, currentUser, navigate, id]);

    const handleSubmit = useCallback((data: {
        nickname: string;
        name?: string | null;
        email?: string | null;
        is_active?: boolean;
        comment?: string | null;
        role: string;
        password?: string | null;
        password_confirmation?: string | null;
        new_password?: boolean | null;
    }) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            updateUser({
                ...formData,
                is_active: true,
            }, {
                onSuccess: () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    formRef.current?.reset();
                },
            });
        }
    }, [formData, updateUser]);

    const handleCancel = useCallback(() => {
        navigate(`${AppRoute.Users}/${id}`);
    }, [navigate, id]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Редагувати користувача</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={12}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Users}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Редагувати користувача
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <UserForm
                            ref={formRef}
                            initData={user || null}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isLoading || isPending}
                            cancelTo={`${AppRoute.Users}/${id}`}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="user"
                entityName={formData?.nickname || formData?.email || user?.nickname || user?.email}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
